import React from 'react';
import {
  Box,
  Chip,
  IconButton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Edit as EditIcon,
  ContentCopy as CloneIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { formatDate } from '../../utils/dateUtils';
import { formatEventLabel, RULES_TABLE_GRID_COLS, RULES_TABLE_MIN_WIDTH } from './constants';

const actionButtons = (rule, { onEdit, onDuplicate, onDelete }) => (
  <Stack direction="row" alignItems="center" spacing={0} sx={{ flexShrink: 0 }}>
    <Tooltip title="Edit">
      <IconButton size="small" onClick={() => onEdit(rule)} aria-label="Edit rule">
        <EditIcon fontSize="small" sx={{ color: '#40518A' }} />
      </IconButton>
    </Tooltip>
    <Tooltip title="Clone">
      <IconButton size="small" onClick={() => onDuplicate(rule)} aria-label="Clone rule">
        <CloneIcon fontSize="small" sx={{ color: '#64748b' }} />
      </IconButton>
    </Tooltip>
    <Tooltip title="Delete">
      <IconButton size="small" onClick={() => onDelete(rule)} aria-label="Delete rule">
        <DeleteIcon fontSize="small" sx={{ color: '#ef4444' }} />
      </IconButton>
    </Tooltip>
  </Stack>
);

const MobileMetaRow = ({ label, children }) => (
  <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ minWidth: 0 }}>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ fontWeight: 600, minWidth: 88, flexShrink: 0, pt: 0.1 }}
    >
      {label}
    </Typography>
    <Box sx={{ minWidth: 0, flex: 1 }}>{children}</Box>
  </Stack>
);

const RuleCard = ({
  rule,
  toggling = false,
  onToggleActive,
  onEdit,
  onDuplicate,
  onDelete,
}) => {
  const eventLabel = formatEventLabel(rule.event) || rule.event;
  const actions = { onEdit, onDuplicate, onDelete };

  return (
    <Box
      sx={{
        borderBottom: '1px solid',
        borderColor: 'divider',
        transition: 'background 0.15s',
        '&:hover': { bgcolor: '#f8fafc' },
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      {/* Mobile card */}
      <Box
        sx={{
          display: { xs: 'block', sm: 'none' },
          px: 2,
          py: 2,
        }}
      >
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, mb: 0.5, wordBreak: 'break-word' }}
            >
              {rule.name}
            </Typography>
            <Chip
              label={eventLabel}
              size="small"
              sx={{ fontSize: '0.7rem', height: 20, bgcolor: '#e8edf7', color: '#40518A', fontWeight: 600 }}
            />
          </Box>
          {actionButtons(rule, actions)}
        </Stack>

        <Stack spacing={1.25}>
          <MobileMetaRow label="Description">
            <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
              {rule.description || '—'}
            </Typography>
          </MobileMetaRow>

          <MobileMetaRow label="Status">
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: rule.is_active ? 'success.main' : 'text.disabled' }}
              >
                {rule.is_active ? 'Active' : 'Inactive'}
              </Typography>
              <Switch
                checked={Boolean(rule.is_active)}
                onChange={() => onToggleActive(rule.id, rule.is_active)}
                disabled={toggling}
                color="primary"
                size="small"
              />
            </Stack>
          </MobileMetaRow>

          <MobileMetaRow label="Created">
            <Typography variant="caption" color="text.secondary">
              {rule.created_at ? formatDate(rule.created_at, false) : '—'}
            </Typography>
          </MobileMetaRow>

          <MobileMetaRow label="Created by">
            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
              {rule.created_by || '—'}
            </Typography>
          </MobileMetaRow>

          <MobileMetaRow label="Updated by">
            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
              {rule.updated_by || '—'}
            </Typography>
          </MobileMetaRow>
        </Stack>
      </Box>

      {/* Desktop / tablet table row */}
      <Box
        sx={{
          display: { xs: 'none', sm: 'grid' },
          gridTemplateColumns: RULES_TABLE_GRID_COLS,
          alignItems: 'center',
          gap: 2,
          px: 3,
          py: 2,
          minWidth: RULES_TABLE_MIN_WIDTH,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, mb: 0.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            title={rule.name}
          >
            {rule.name}
          </Typography>
          <Chip
            label={eventLabel}
            size="small"
            sx={{ fontSize: '0.7rem', height: 20, bgcolor: '#e8edf7', color: '#40518A', fontWeight: 600 }}
          />
        </Box>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          title={rule.description}
        >
          {rule.description || '—'}
        </Typography>

        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: rule.is_active ? 'success.main' : 'text.disabled' }}>
            {rule.is_active ? 'Active' : 'Inactive'}
          </Typography>
          <Switch
            checked={Boolean(rule.is_active)}
            onChange={() => onToggleActive(rule.id, rule.is_active)}
            disabled={toggling}
            color="primary"
            size="small"
          />
        </Stack>

        <Typography variant="caption" color="text.secondary">
          {rule.created_at ? formatDate(rule.created_at, false) : '—'}
        </Typography>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          title={rule.created_by || ''}
        >
          {rule.created_by || '—'}
        </Typography>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          title={rule.updated_by || ''}
        >
          {rule.updated_by || '—'}
        </Typography>

        {actionButtons(rule, actions)}
      </Box>
    </Box>
  );
};

export default RuleCard;
